import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUp, faSparkles, faStop } from '@fortawesome/pro-regular-svg-icons';

import { Button } from 'components/ui/button';
import { InputGroup } from '@buildoutinc/blueprint-react/ui/InputGroup';
import { Textarea } from '@buildoutinc/blueprint-react/ui/Textarea';
import { InstructionPresets } from './instruction_presets';

interface Props {
  generating: boolean;
  hasText: boolean;
  instruction: string;
  onChange: (instruction: string) => void;
  onPreset: (instruction: string) => void;
  onStop: () => void;
  onSubmit: () => void;
}

export function Instructions({ generating, hasText, instruction, onChange, onPreset, onStop, onSubmit }: Props) {
  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      onSubmit();
    }
  }

  let trailing: React.ReactNode;
  if (generating) {
    trailing = <Button aria-label="Stop generating" icon={faStop} size="sm" variant="ghost" onClick={onStop} />;
  } else if (instruction.trim()) {
    trailing = <Button aria-label="Generate" icon={faArrowUp} size="sm" variant="ghost" onClick={onSubmit} />;
  } else if (hasText) {
    trailing = <InstructionPresets onSelect={onPreset} />;
  }

  return (
    <InputGroup className="ai-field-instructions">
      <InputGroup.Addon>
        <FontAwesomeIcon className="text-purple-heart-500" icon={faSparkles} />
      </InputGroup.Addon>

      {/* While generating, the label stands in for the input and takes its styling, so the row keeps
          the input's own background rather than letting the host page show through. */}
      {generating ? (
        <span className="form-control text-muted">Generating…</span>
      ) : (
        <Textarea
          autoFocus
          placeholder={hasText ? 'Describe your change' : 'What should be written?'}
          rows={1}
          value={instruction}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
        />
      )}

      <InputGroup.Addon>
        {/* Hidden rather than absent, so the row keeps one height whether or not it has an action. */}
        {trailing ?? <Button aria-hidden className="invisible" icon={faArrowUp} size="sm" variant="ghost" />}
      </InputGroup.Addon>
    </InputGroup>
  );
}
